// 报告选择器 / Report picker.
// 根据报告数量决定禁用、直开或展示选择浮层。
// Chooses between disabled, direct open, or a picker modal based on report count.

// 报告选择项 / Report picker item.
export interface ReportPickerItem {
  report_id: string;
  title: string;
  viewer_url?: string;
  created_at?: string | null;
}

// 报告选择结果 / Result returned after opening reports.
export type ReportPickerResult =
  | { status: "disabled" }
  | { status: "opened"; reportId: string }
  | { status: "picker"; count: number };

let pickerEvents: AbortController | null = null;
let restoreFocusTarget: HTMLElement | null = null;

/**
 * 打开当前选择的报告 / Open a report or render a report picker.
 */
export function openReportForSelection(
  reports: ReportPickerItem[],
  windowOpen: (url: string, target?: string) => void = (url, target) => window.open(url, target),
): ReportPickerResult {
  if (reports.length === 0) {
    closeReportPicker();
    return { status: "disabled" };
  }
  if (reports.length === 1) {
    const report = reports[0]!;
    windowOpen(reportUrl(report), "_blank");
    closeReportPicker();
    return { status: "opened", reportId: report.report_id };
  }

  renderReportPicker(reports, windowOpen);
  return { status: "picker", count: reports.length };
}

/**
 * 关闭报告选择器 / Close the report picker.
 */
export function closeReportPicker(): void {
  const root = document.getElementById("report-picker-root");
  if (root) {
    root.replaceChildren();
    root.hidden = true;
  }
  pickerEvents?.abort();
  pickerEvents = null;
  document.body.classList.remove("report-picker-open");
  restoreFocusTarget?.focus();
  restoreFocusTarget = null;
}

// 渲染多报告选择浮层 / Render the multi-report picker modal.
function renderReportPicker(
  reports: ReportPickerItem[],
  windowOpen: (url: string, target?: string) => void,
): void {
  const root = pickerRoot();
  restoreFocusTarget = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  pickerEvents?.abort();
  pickerEvents = new AbortController();
  const { signal } = pickerEvents;

  const overlay = document.createElement("div");
  overlay.className = "report-picker-overlay";
  overlay.setAttribute("aria-hidden", "true");

  const panel = document.createElement("section");
  panel.className = "report-picker-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-labelledby", "report-picker-title");

  const header = document.createElement("header");
  header.className = "report-picker-header";
  const title = document.createElement("h2");
  title.id = "report-picker-title";
  title.textContent = "Choose Report";
  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "report-picker-close";
  closeButton.setAttribute("aria-label", "Close report picker");
  closeButton.textContent = "×";
  closeButton.addEventListener("click", closeReportPicker, { signal });
  header.append(title, closeButton);

  const list = document.createElement("div");
  list.className = "report-picker-list";
  for (const report of reports) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "report-picker-item";
    button.dataset.reportId = report.report_id;
    const name = document.createElement("strong");
    name.textContent = report.title || report.report_id;
    const meta = document.createElement("span");
    meta.textContent = report.created_at ? formatDate(report.created_at) : report.report_id;
    button.append(name, meta);
    button.addEventListener("click", () => {
      windowOpen(reportUrl(report), "_blank");
      closeReportPicker();
    }, { signal });
    list.append(button);
  }

  panel.append(header, list);
  root.replaceChildren(overlay, panel);
  root.hidden = false;
  document.body.classList.add("report-picker-open");
  closeButton.focus();
}

// 获取报告 URL / Resolve the report viewer URL.
function reportUrl(report: ReportPickerItem): string {
  return report.viewer_url || `/reports/${encodeURIComponent(report.report_id)}`;
}

// 日期展示 / Display timestamp.
function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return date.toLocaleString();
}

// 获取挂载点 / Resolve the picker root.
function pickerRoot(): HTMLElement {
  const root = document.getElementById("report-picker-root");
  if (!root) throw new Error("#report-picker-root mount missing");
  return root;
}
